import { LogoutOutlined, ThunderboltOutlined, UserOutlined, WalletOutlined } from '@ant-design/icons';
import { history, useModel, useRequest } from '@umijs/max';
import { Avatar, Button, Dropdown, message, Space, theme } from 'antd';
import type { MenuProps } from 'antd';
import React from 'react';
import { flushSync } from 'react-dom';
import { loginModal } from '@/components/LoginModal';
import { paymentModal } from '@/components/PaymentModal';
import { SelectLang } from '@/components/RightContent';
import {
  DASHBOARD_DATA_CACHE_KEY,
  extractDashboardLedgerBalance,
  fetchDashboardData,
} from '@/services/dashboard';
import { outLogin } from '@/services/auth';
import { formatLocaleMessage as t, getAppLocale } from '@/utils/i18n';

const SiderFooter: React.FC<{ collapsed?: boolean }> = ({ collapsed }) => {
  const { token } = theme.useToken();
  const { initialState, setInitialState } = useModel('@@initialState');

  const isLoggedIn = !!initialState?.currentUser;
  const userId = initialState?.currentUser?.user?.id;
  const { data: dashboardData } = useRequest(
    () => fetchDashboardData({ skipErrorHandler: true }),
    {
      ready: Boolean(userId),
      cacheKey: DASHBOARD_DATA_CACHE_KEY,
      staleTime: 5_000,
      refreshOnWindowFocus: false,
    },
  );

  const balance = React.useMemo(() => {
    if (!userId) {
      return 0;
    }
    return extractDashboardLedgerBalance(dashboardData);
  }, [dashboardData, userId]);

  const balanceText = React.useMemo(() => {
    const amount = Number(balance || 0);
    if (!Number.isFinite(amount)) {
      return '0';
    }

    return new Intl.NumberFormat(getAppLocale(), {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }, [balance]);

  const loginOut = async () => {
    localStorage.removeItem('token');
    await outLogin();
    message.success(t('components.siderFooter.logoutSuccess', 'Logged out successfully'));
    history.replace('/home');
  };

  const onMenuClick: MenuProps['onClick'] = ({ key }) => {
    if (key !== 'logout') {
      return;
    }

    flushSync(() => {
      setInitialState((state) => ({ ...state, currentUser: undefined }));
    });

    loginOut();
  };

  const menuItems: MenuProps['items'] = [
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: t('components.siderFooter.logout', 'Logout'),
    },
  ];

  const userDropdown = (
    <Dropdown menu={{ items: menuItems, onClick: onMenuClick }} placement="topRight" trigger={['click']}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: collapsed ? '8px' : '6px 8px',
          cursor: 'pointer',
          borderRadius: token.borderRadius,
          transition: 'background-color 0.2s',
        }}
        onMouseEnter={(event) => {
          event.currentTarget.style.backgroundColor = token.colorBgTextHover;
        }}
        onMouseLeave={(event) => {
          event.currentTarget.style.backgroundColor = 'transparent';
        }}
      >
        <Avatar
          size={28}
          icon={<UserOutlined />}
          src={initialState?.currentUser?.user?.avatar}
          style={{ backgroundColor: token.colorPrimary }}
        />
        {!collapsed && (
          <span style={{ fontSize: 14, fontWeight: 500 }}>
            {initialState?.currentUser?.user?.name || t('components.siderFooter.notLoggedIn', 'Not signed in')}
          </span>
        )}
      </div>
    </Dropdown>
  );

  if (collapsed) {
    return (
      <div
        style={{
          padding: '12px 8px',
          borderTop: `1px solid ${token.colorBorderSecondary}`,
        }}
      >
        <Space direction="vertical" style={{ width: '100%' }} align="center">
          <div
            style={{
              padding: '8px',
              cursor: 'pointer',
              borderRadius: token.borderRadius,
              transition: 'background-color 0.2s',
            }}
            onClick={() => paymentModal.show({ defaultTab: 'plans' })}
            onMouseEnter={(event) => {
              event.currentTarget.style.backgroundColor = token.colorBgTextHover;
            }}
            onMouseLeave={(event) => {
              event.currentTarget.style.backgroundColor = 'transparent';
            }}
            title={t('components.siderFooter.subscriptionAndRecharge', 'Subscription & Recharge')}
          >
            <Avatar size={28} icon={<WalletOutlined />} style={{ backgroundColor: token.colorPrimary }} />
          </div>
          <SelectLang />
          {isLoggedIn ? (
            userDropdown
          ) : (
            <div
              style={{
                padding: '8px',
                cursor: 'pointer',
                borderRadius: token.borderRadius,
                transition: 'background-color 0.2s',
              }}
              onClick={() => loginModal.show()}
              onMouseEnter={(event) => {
                event.currentTarget.style.backgroundColor = token.colorBgTextHover;
              }}
              onMouseLeave={(event) => {
                event.currentTarget.style.backgroundColor = 'transparent';
              }}
              title={t('components.siderFooter.login', 'Login')}
            >
              <Avatar size={28} icon={<UserOutlined />} style={{ backgroundColor: token.colorPrimary }} />
            </div>
          )}
        </Space>
      </div>
    );
  }

  const iconSlot: React.CSSProperties = {
    width: 28,
    height: 28,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    fontSize: 16,
  };

  const rowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '6px 8px',
    cursor: 'pointer',
    borderRadius: token.borderRadius,
    transition: 'background-color 0.2s',
  };

  const handleRowHoverEnter = (event: React.MouseEvent<HTMLDivElement>) => {
    event.currentTarget.style.backgroundColor = token.colorBgTextHover;
  };

  const handleRowHoverLeave = (event: React.MouseEvent<HTMLDivElement>) => {
    event.currentTarget.style.backgroundColor = 'transparent';
  };

  return (
    <div
      style={{
        padding: '8px 4px',
        borderTop: `1px solid ${token.colorBorderSecondary}`,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '6px 8px',
          borderRadius: token.borderRadius,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ ...iconSlot, color: token.colorPrimary }}>
            <ThunderboltOutlined />
          </span>
          <span style={{ fontSize: 14, fontWeight: 500 }}>{balanceText}</span>
        </div>
        <Button
          type="primary"
          size="small"
          style={{
            borderRadius: 16,
            fontSize: 12,
            height: 26,
            padding: '0 12px',
          }}
          onClick={() => paymentModal.show({ defaultTab: 'recharge' })}
        >
          {t('components.siderFooter.subscribeRecharge', 'Subscribe / Recharge')}
        </Button>
      </div>

      <SelectLang />

      {isLoggedIn ? (
        userDropdown
      ) : (
        <div
          style={{ ...rowStyle, color: token.colorTextSecondary }}
          onClick={() => loginModal.show()}
          onMouseEnter={handleRowHoverEnter}
          onMouseLeave={handleRowHoverLeave}
        >
          <span style={iconSlot}>
            <UserOutlined />
          </span>
          <span style={{ fontSize: 14 }}>{t('components.siderFooter.login', 'Login')}</span>
        </div>
      )}
    </div>
  );
};

export default SiderFooter;
